import { ChevronDown, ChevronRight, Loader2, Wrench } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { ChatImageGallery } from './ChatImageGallery';
import type { AdviceOption, ChatBlock, ChatImage } from './types';

interface TalkChatBlocksProps {
  blocks: ChatBlock[];
  streamingBuffer: {
    type: ChatBlock['type'];
    content: string;
    images?: ChatImage[];
  };
  pendingTools: string[];
  codingActions: { tool: string; label: string }[];
  codingStats: { reads: number; edits: number; commands: number } | null;
  statusMessage: string;
  processing: boolean;
  backendBusy: boolean;
  aiTipMessage: string;
  onToggleCollapse: (id: number) => void;
  onSubmitPrompt: (content: string) => void | Promise<unknown>;
}

function AdviceCard({
  option,
  onSubmitPrompt,
}: {
  option: AdviceOption;
  onSubmitPrompt: (content: string) => void | Promise<unknown>;
}) {
  return (
    <button
      onClick={() => onSubmitPrompt(`Apply #${option.id}`)}
      className="w-full rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-background"
    >
      <span className="font-medium">
        {option.id === 1
          ? 'Recommended'
          : option.id === 2
            ? 'Alternative'
            : 'Advanced'}{' '}
        - {option.title}
      </span>
      <span className="ml-2 text-xs text-muted-foreground">({option.tag})</span>
      <p className="mt-1 text-xs text-muted-foreground">
        Outcome: {option.expectedOutcome}
      </p>
      <p className="text-xs text-muted-foreground">
        Risk: {option.risk} · Effort: {option.effort}
      </p>
    </button>
  );
}

function RenderBlock({
  block,
  onToggleCollapse,
  onSubmitPrompt,
}: {
  block: ChatBlock;
  onToggleCollapse: (id: number) => void;
  onSubmitPrompt: (content: string) => void | Promise<unknown>;
}) {
  switch (block.type) {
    case 'user':
      return (
        <div key={block.id} className="flex justify-end">
          <div className="max-w-[88%] rounded-2xl bg-primary px-4 py-3 text-sm text-primary-foreground shadow-sm">
            <div className="whitespace-pre-wrap">{block.content}</div>
            {block.images ? (
              <ChatImageGallery images={block.images} alt="Attached" />
            ) : null}
          </div>
        </div>
      );
    case 'agent-text':
      return (
        <div key={block.id} className="flex justify-start">
          <div className="max-w-[88%] rounded-2xl border bg-card px-4 py-3 text-sm shadow-sm">
            <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-pre:my-2 dark:prose-invert">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {block.content}
              </ReactMarkdown>
            </div>
            {block.images ? (
              <ChatImageGallery images={block.images} alt="Evidence" />
            ) : null}
          </div>
        </div>
      );
    case 'tool-group':
      return (
        <div
          key={block.id}
          className="flex flex-wrap items-center gap-2 px-1 py-1 opacity-80"
        >
          <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
          {(block.tools || []).map((tool, index) => (
            <span
              key={`${tool}-${index}`}
              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            >
              {tool === 'delegate_to_code_ai' ? 'Code AI working' : tool}
            </span>
          ))}
        </div>
      );
    case 'tool-result':
      return null;
    case 'tool-activity':
      return (
        <div
          key={block.id}
          className="overflow-hidden rounded-2xl border bg-muted/40"
        >
          <button
            onClick={() => onToggleCollapse(block.id)}
            className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/80"
          >
            {block.collapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            <Wrench className="h-3 w-3" />
            <span>Tool activity</span>
            <span className="ml-auto text-[10px] opacity-60">
              {(block.executions || []).length} task(s)
            </span>
          </button>
          {!block.collapsed ? (
            <div className="space-y-2 border-t bg-background/60 px-3 py-2">
              {(block.executions || []).map((execution) => (
                <div
                  key={execution.id}
                  className="rounded-xl border bg-background/70 px-3 py-2 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {execution.label || execution.tool}
                    </span>
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] ${
                        execution.status === 'succeeded'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : execution.status === 'failed'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : execution.status === 'cancelled'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}
                    >
                      {execution.status}
                    </span>
                    {typeof execution.durationMs === 'number' ? (
                      <span className="text-muted-foreground">
                        {execution.durationMs}ms
                      </span>
                    ) : null}
                  </div>
                  {execution.inputSummary ? (
                    <p className="mt-1 text-muted-foreground">
                      Input: {execution.inputSummary}
                    </p>
                  ) : null}
                  {execution.outputSummary ? (
                    <p className="mt-1 text-muted-foreground">
                      Output: {execution.outputSummary}
                    </p>
                  ) : null}
                  {execution.errorCode ? (
                    <p className="mt-1 text-red-600 dark:text-red-400">
                      Error: {execution.errorCode}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      );
    case 'error':
      return (
        <div
          key={block.id}
          className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {block.content}
        </div>
      );
    case 'advice':
      return (
        <div
          key={block.id}
          className="space-y-2 rounded-2xl border bg-muted/40 p-3"
        >
          <p className="text-sm font-medium">{block.content}</p>
          <div className="space-y-2">
            {(block.options || []).slice(0, 3).map((option) => (
              <AdviceCard
                key={option.id}
                option={option}
                onSubmitPrompt={onSubmitPrompt}
              />
            ))}
          </div>
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSubmitPrompt('Ignore')}
              className="h-7 text-xs"
            >
              Ignore
            </Button>
          </div>
        </div>
      );
    case 'reasoning-summary':
      return (
        <div
          key={block.id}
          className="overflow-hidden rounded-xl border bg-muted/30"
        >
          <button
            type="button"
            onClick={() => onToggleCollapse(block.id)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {block.collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            <Loader2
              className={`h-3.5 w-3.5 ${block.collapsed ? '' : 'animate-spin'}`}
            />
            <span className="font-medium">Progress</span>
            <span className="ml-auto text-[10px] opacity-70">
              {(block.entries || [block.content]).length} updates
            </span>
          </button>
          {!block.collapsed && (
            <ol className="space-y-2 border-t px-4 py-3 text-xs text-muted-foreground">
              {(block.entries || [block.content]).map((entry, index) => (
                <li key={`${index}-${entry}`} className="flex gap-2">
                  <span className="mt-1.5 size-1 shrink-0 rounded-full bg-primary" />
                  <span className="min-w-0 break-words">{entry}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      );
  }
}

export function TalkChatBlocks({
  blocks,
  streamingBuffer,
  pendingTools,
  codingActions,
  codingStats,
  statusMessage,
  processing,
  backendBusy,
  aiTipMessage,
  onToggleCollapse,
  onSubmitPrompt,
}: TalkChatBlocksProps) {
  const displayBusyMessage =
    statusMessage || 'AI is working on your request...';
  const showAiTip =
    Boolean(aiTipMessage) && aiTipMessage.trim() !== displayBusyMessage.trim();
  const hasStreamingContent = Boolean(streamingBuffer.content);
  const hasDelegation = pendingTools.includes('delegate_to_code_ai');

  return (
    <>
      {blocks.length === 0 && !hasStreamingContent && !processing ? (
        <div className="rounded-2xl border border-dashed bg-card/60 px-5 py-8 text-center text-sm text-muted-foreground">
          Start a PM conversation for this running GIFT Agent session.
        </div>
      ) : null}

      {blocks.map((block) => (
        <RenderBlock
          key={block.id}
          block={block}
          onToggleCollapse={onToggleCollapse}
          onSubmitPrompt={onSubmitPrompt}
        />
      ))}

      {pendingTools.length > 0 ? (
        hasDelegation && codingActions.length > 0 ? (
          <div className="overflow-hidden rounded-2xl border bg-muted/30">
            <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                Code AI working
              </span>
              {codingStats ? (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {codingStats.reads > 0
                    ? `${codingStats.reads} read${codingStats.reads > 1 ? 's' : ''}`
                    : ''}
                  {codingStats.edits > 0
                    ? `${codingStats.reads > 0 ? ' · ' : ''}${codingStats.edits} edit${codingStats.edits > 1 ? 's' : ''}`
                    : ''}
                  {codingStats.commands > 0
                    ? `${codingStats.reads > 0 || codingStats.edits > 0 ? ' · ' : ''}${codingStats.commands} cmd${codingStats.commands > 1 ? 's' : ''}`
                    : ''}
                </span>
              ) : null}
            </div>
            <div className="max-h-28 space-y-1 overflow-y-auto px-3 py-2">
              {codingActions.slice(-5).map((action, index) => (
                <div
                  key={`${action.tool}-${index}`}
                  className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                >
                  <span className="h-1 w-1 rounded-full bg-blue-400" />
                  <span className="truncate">{action.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5 px-1 py-1">
            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
            {pendingTools.map((tool, index) => (
              <span
                key={`${tool}-${index}`}
                className="inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                {tool === 'delegate_to_code_ai'
                  ? statusMessage || 'AI is handling this step...'
                  : tool}
              </span>
            ))}
          </div>
        )
      ) : null}

      {hasStreamingContent ? (
        streamingBuffer.type === 'agent-text' ? (
          <div className="flex justify-start">
            <div className="max-w-[88%] rounded-2xl border bg-card px-4 py-3 text-sm shadow-sm">
              <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-pre:my-2 dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {streamingBuffer.content}
                </ReactMarkdown>
              </div>
              <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse align-middle bg-foreground/40" />
            </div>
          </div>
        ) : null
      ) : null}

      {(processing || backendBusy) &&
      !hasStreamingContent &&
      pendingTools.length === 0 ? (
        <div className="space-y-1.5 px-1 py-2">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {displayBusyMessage}
            </span>
          </div>
          {showAiTip ? (
            <p className="pl-6 text-xs text-muted-foreground/90">
              {aiTipMessage}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
